import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Alert,
  Divider,
  Chip,
  useTheme
} from '@mui/material';
import { useTheme as useCustomTheme } from '../theme/ThemeProvider';
import aiLogger from '../utils/aiLogger';

const LoginConfig = ({ selectedProject, onConfigComplete, apiClient }) => {
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    username_selector: '',
    password_selector: '',
    login_button_selector: '',
    success_indicator: '',
    auto_detect_selectors: true,
    login_url: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const theme = useTheme();
  const { isDarkMode } = useCustomTheme();

  const loadExistingConfig = useCallback(async () => {
    if (!selectedProject) return;
    try {
      const config = await apiClient.getLoginConfig(selectedProject.project_id);
      setFormData({
        username: config.username || '',
        password: config.password || '',
        username_selector: config.username_selector || '',
        password_selector: config.password_selector || '',
        login_button_selector: config.login_button_selector || '',
        success_indicator: config.success_indicator || '',
        auto_detect_selectors: config.auto_detect_selectors !== undefined ? config.auto_detect_selectors : true,
        login_url: config.login_url || ''
      });
    } catch (error) {
      console.error('Error loading config:', error);
      setError('Failed to load existing configuration');
    }
  }, [selectedProject, apiClient]);

  useEffect(() => {
    if (selectedProject) {
      loadExistingConfig();
    }
  }, [selectedProject, loadExistingConfig]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const applyDefaultSelectors = () => {
    const defaults = {
      saucedemo: {
        username_selector: '#user-name',
        password_selector: '#password',
        login_button_selector: '#login-button',
        success_indicator: '.inventory_container'
      },
      orangehrm: {
        username_selector: 'input[name="username"]',
        password_selector: 'input[name="password"]',
        login_button_selector: 'button[type="submit"]',
        success_indicator: '.oxd-main-menu'
      }
    };

    const projectDefaults = defaults[selectedProject.project_type] || {
      username_selector: 'input[placeholder*="username"], input[name="username"]',
      password_selector: 'input[placeholder*="password"], input[name="password"]',
      login_button_selector: 'button[type="submit"], button:has-text("Login")',
      success_indicator: '.dashboard, .main-content'
    };

    setFormData(prev => ({
      ...prev,
      ...projectDefaults
    }));
  };

  const handleSave = async () => {
    setLoading(true);
    setError(null);
    
    try {
      aiLogger.debug('Saving config:', formData);
      
      await apiClient.updateLoginConfig(selectedProject.project_id, formData);
      onConfigComplete(formData);
    } catch (error) {
      aiLogger.error('Error saving config:', error);
      setError('Failed to save configuration. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = () => {
    onConfigComplete();
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: isDarkMode 
          ? 'linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 100%)'
          : 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
        py: 4
      }}
    >
      <Container maxWidth="md">
        <Box sx={{ maxWidth: 800, mx: 'auto' }}>
          {/* Header */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography variant="h3" component="h1" fontWeight={600} gutterBottom>
              Login Configuration
            </Typography>
            <Typography variant="h6" color="text.secondary">
              Configure login settings for {selectedProject?.project_name}
            </Typography>
          </Box>

          <Card
            elevation={8}
            sx={{
              borderRadius: 3,
              overflow: 'hidden',
              background: theme.palette.background.paper,
              border: `1px solid ${theme.palette.divider}`
            }}
          >
            <CardContent sx={{ p: 4 }}>
              {/* Project Info */}
              <Box sx={{ mb: 4, p: 3, background: theme.palette.action.hover, borderRadius: 2 }}>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Project Details
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Chip
                    label={`Type: ${selectedProject?.project_type || 'Custom'}`}
                    color="primary"
                    variant="outlined"
                  />
                  <Chip
                    label={`URL: ${selectedProject?.base_url}`}
                    variant="outlined"
                    sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
                  />
                </Box>
              </Box>

              {/* Error Alert */}
              {error && (
                <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                  {error}
                </Alert>
              )}

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {/* Auto-detect Switch */}
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.auto_detect_selectors}
                      onChange={(e) => handleInputChange('auto_detect_selectors', e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Auto-detect selectors (recommended)"
                />

                <Divider />

                {/* Login URL */}
                <TextField
                  fullWidth
                  label="Login URL (optional)"
                  value={formData.login_url}
                  onChange={(e) => handleInputChange('login_url', e.target.value)}
                  placeholder="https://example.com/login"
                  variant="outlined"
                  helperText="Leave empty to use the project's base URL"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    }
                  }}
                />

                {/* Credentials Section */}
                <Box>
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    Login Credentials
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <TextField
                      label="Username"
                      value={formData.username}
                      onChange={(e) => handleInputChange('username', e.target.value)}
                      variant="outlined"
                      sx={{ flex: 1, minWidth: 200, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                    <TextField
                      label="Password"
                      type="password"
                      value={formData.password}
                      onChange={(e) => handleInputChange('password', e.target.value)}
                      variant="outlined"
                      sx={{ flex: 1, minWidth: 200, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                  </Box>
                </Box>

                <Divider />

                {/* Selectors Section */}
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="h6" fontWeight={600}>
                      CSS Selectors
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={applyDefaultSelectors}
                      sx={{ borderRadius: 2 }}
                    >
                      Apply Defaults
                    </Button>
                  </Box>
                  
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <TextField
                      fullWidth
                      label="Username Field Selector"
                      value={formData.username_selector}
                      onChange={(e) => handleInputChange('username_selector', e.target.value)}
                      placeholder="input[name='username'], #username"
                      variant="outlined"
                      helperText="CSS selector for the username input field"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                        }
                      }}
                    />
                    
                    <TextField
                      fullWidth
                      label="Password Field Selector"
                      value={formData.password_selector}
                      onChange={(e) => handleInputChange('password_selector', e.target.value)}
                      placeholder="input[name='password'], #password"
                      variant="outlined"
                      helperText="CSS selector for the password input field"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                        }
                      }}
                    />
                    
                    <TextField
                      fullWidth
                      label="Login Button Selector"
                      value={formData.login_button_selector}
                      onChange={(e) => handleInputChange('login_button_selector', e.target.value)}
                      placeholder="button[type='submit'], .login-btn"
                      variant="outlined"
                      helperText="CSS selector for the login button"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                        }
                      }}
                    />
                    
                    <TextField
                      fullWidth
                      label="Success Indicator Selector"
                      value={formData.success_indicator}
                      onChange={(e) => handleInputChange('success_indicator', e.target.value)}
                      placeholder=".dashboard, .main-content"
                      variant="outlined"
                      helperText="CSS selector that appears after successful login"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                        }
                      }}
                    />
                  </Box>
                </Box>
              </Box>

              {/* Action Buttons */}
              <Box sx={{ display: 'flex', gap: 2, mt: 4, pt: 3, borderTop: `1px solid ${theme.palette.divider}` }}>
                <Button
                  variant="outlined"
                  onClick={handleSkip}
                  sx={{ borderRadius: 2, px: 4 }}
                >
                  Skip Configuration
                </Button>
                <Box sx={{ flex: 1 }} />
                <Button
                  variant="contained"
                  onClick={handleSave}
                  disabled={loading}
                  sx={{
                    borderRadius: 2,
                    px: 4,
                    background: 'linear-gradient(135deg, #0099F7 0%, #0052D4 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #0052D4 0%, #003d82 100%)',
                    }
                  }}
                >
                  {loading ? 'Saving...' : 'Save Configuration'}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Container>
    </Box>
  );
};

export default LoginConfig; 